import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { Page } from 'react-pdf';
import type { PageCallback } from 'react-pdf/dist/cjs/shared/types';

import type { ESignPageDimensions } from './types';

// Larger trips iPad Safari's canvas memory cap; smaller leaves blank gaps on fast scrolls.
const VIEWPORT_BUFFER_PX = 1500;

// 3x devices (iPad Pro) would otherwise allocate ~34 MB per Letter page.
const MAX_CANVAS_DEVICE_PIXEL_RATIO = 2;

interface ESignPdfPageProps {
  pageNumber: number;
  width: number;
  onRenderSuccess: (pageNumber: number, width: number, height: number) => void;
  pageDimensions: ESignPageDimensions | undefined;
  fallbackAspectRatio: number;
  renderOverlay?: (
    pageNumber: number,
    dimensions: ESignPageDimensions
  ) => React.ReactNode;
}

export function ESignPdfPage({
  pageNumber,
  width,
  onRenderSuccess,
  pageDimensions,
  fallbackAspectRatio,
  renderOverlay
}: ESignPdfPageProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  // Page 1 starts mounted so the first paint isn't blank during IO setup.
  const [isVisible, setIsVisible] = useState(pageNumber === 1);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) {
      return;
    }

    if (typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          setIsVisible(entry.isIntersecting);
        }
      },
      {
        rootMargin: `${VIEWPORT_BUFFER_PX}px 0px ${VIEWPORT_BUFFER_PX}px 0px`
      }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleRenderSuccess = useCallback(
    (page: PageCallback) => {
      onRenderSuccess(pageNumber, page.width, page.height);
    },
    [pageNumber, onRenderSuccess]
  );

  // Rebuild against current width — zoom and ResizeObserver can change
  // width after the cached entry was captured.
  const overlayDimensions = useMemo<ESignPageDimensions>(() => {
    const aspectRatio =
      pageDimensions && pageDimensions.renderedWidth > 0
        ? pageDimensions.renderedHeight / pageDimensions.renderedWidth
        : fallbackAspectRatio;
    return {
      pageNumber,
      renderedWidth: width,
      renderedHeight: Math.round(width * aspectRatio)
    };
  }, [pageNumber, width, pageDimensions, fallbackAspectRatio]);

  return (
    <div
      ref={containerRef}
      data-esign-pdf-page-number={pageNumber}
      style={{
        position: 'relative',
        marginBottom: 4,
        width: overlayDimensions.renderedWidth,
        minHeight: overlayDimensions.renderedHeight
      }}
    >
      {isVisible ? (
        <Page
          pageNumber={pageNumber}
          width={width}
          devicePixelRatio={Math.min(
            MAX_CANVAS_DEVICE_PIXEL_RATIO,
            (typeof window !== 'undefined' && window.devicePixelRatio) || 1
          )}
          renderTextLayer={false}
          renderAnnotationLayer={false}
          onRenderSuccess={handleRenderSuccess}
        />
      ) : (
        <div
          aria-hidden
          style={{
            width: overlayDimensions.renderedWidth,
            height: overlayDimensions.renderedHeight,
            backgroundColor: '#ffffff',
            boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.06)'
          }}
        />
      )}
      {renderOverlay && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: overlayDimensions.renderedWidth,
            height: overlayDimensions.renderedHeight,
            pointerEvents: 'none'
          }}
        >
          {renderOverlay(pageNumber, overlayDimensions)}
        </div>
      )}
    </div>
  );
}
